/**
 * Annotate Customily swatches so short option rows can be styled as
 * the theme's variant buttons (Yes/No, size, font, etc.). Rows with
 * swatch images keep those images inside the button chrome.
 *
 * Customily writes inline !important width/height on swatch labels, which
 * stylesheets cannot override — those locking properties are removed here.
 */
const ROOT_SELECTOR = "#cl_optionsapp";
const BUTTON_GROUP_LIMIT = 5;
const UNLOCK_PROPS = [
  "border",
  "border-color",
  "border-radius",
  "border-style",
  "border-width",
  "height",
  "max-height",
  "max-width",
  "min-height",
  "min-width",
  "padding",
  "width",
];

let applying = false;

const unlock = (element) => {
  if (!element?.style) return;

  UNLOCK_PROPS.forEach((prop) => {
    element.style.removeProperty(prop);
  });
};

const annotate = (root) => {
  if (applying) return;

  applying = true;

  root.querySelectorAll(".swatch-container").forEach((container) => {
    const swatches = container.querySelectorAll(
      ":scope > .swatch, :scope > .customily-swatch",
    );

    container.classList.toggle(
      "is-button-group",
      swatches.length > 0 && swatches.length <= BUTTON_GROUP_LIMIT,
    );

    swatches.forEach((swatch) => {
      const input = swatch.querySelector(
        'input[type="radio"], input[type="checkbox"]',
      );
      const label = swatch.querySelector("label");

      if (input?.value && label) {
        label.dataset.swatchValue = input.value;
      }

      unlock(label);
      unlock(swatch.querySelector(".csh-image-container"));
      unlock(swatch.querySelector("img"));
    });
  });

  applying = false;
};

const observe = (root) => {
  annotate(root);

  const observer = new MutationObserver(() => annotate(root));
  observer.observe(root, {
    attributeFilter: ["style"],
    attributes: true,
    childList: true,
    subtree: true,
  });
};

const start = () => {
  const root = document.querySelector(ROOT_SELECTOR);

  if (root) {
    observe(root);
    return true;
  }

  return false;
};

if (!start()) {
  const ready = new MutationObserver(() => {
    if (start()) ready.disconnect();
  });

  ready.observe(document.documentElement, { childList: true, subtree: true });
}

/**
 * Hide the live-preview canvas when Customily has nothing to render.
 * An empty `.cl-canvas-container` still overlays the gallery and, via
 * `:has(.cl-canvas-container)`, collapses the two-column layout.
 * Preview imagePath is often null even when the mockup has text/image layers.
 */
const PREVIEW_EMPTY_CLASS = "is-customily-preview-empty";
const PREVIEW_EVENTS = [
  "customily-canvas-rendered",
  "customily-live-preview-shown",
  "customily-preview-load-finished",
  "customily-preview-load-started",
  "customily-variation-change-finished",
];

const previewHasContent = (preview) =>
  Boolean(
    preview?.imagePath ||
    preview?.textsPreview?.length ||
    preview?.circularTextsPreview?.length ||
    preview?.imagePlaceHoldersPreview?.length ||
    preview?.vectorEPSPreview?.length,
  );

const previewState = () => {
  const engraver = window.engraver;

  if (!engraver?.initReady || engraver.loadingProduct) return "pending";
  if (!engraver.currentPreview && !engraver.defaultPreview) return "pending";

  return previewHasContent(engraver.currentPreview) ||
    previewHasContent(engraver.defaultPreview)
    ? "ready"
    : "empty";
};

const syncPreviewCanvas = () => {
  const isEmpty = previewState() === "empty";

  document.documentElement.classList.toggle(PREVIEW_EMPTY_CLASS, isEmpty);
  document.querySelectorAll(".product-gallery-wrap").forEach((gallery) => {
    gallery.classList.toggle(PREVIEW_EMPTY_CLASS, isEmpty);
  });

  if (!isEmpty) return;

  const canvas = document.querySelector(".cl-canvas-container");
  if (canvas && getComputedStyle(canvas).display !== "none") {
    window.dispatchEvent(new CustomEvent("customily-live-preview-hide-canvas"));
  }
};

PREVIEW_EVENTS.forEach((eventName) => {
  window.addEventListener(eventName, syncPreviewCanvas);
});

const canvasAdded = (node) =>
  node.nodeType === 1 &&
  (node.matches?.(".cl-canvas-container") ||
    node.querySelector?.(".cl-canvas-container"));

const previewObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (canvasAdded(node)) {
        syncPreviewCanvas();
        return;
      }
    }
  }
});

previewObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});
syncPreviewCanvas();
